import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useCreateJob, useCustomers } from "@/hooks/use-api";
import { jobFields, type JobFormValues } from "./job-form-config";

export default function V2JobCreatePage() {
  const navigate = useNavigate();
  const createJob = useCreateJob();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: JobFormValues) {
    setError(null);
    try {
      const { job } = await createJob.mutateAsync(
        values as Record<string, unknown>,
      );
      const id = (job as Record<string, unknown> | undefined)?.id;
      if (!id) {
        setError("Unexpected response from the server — please try again.");
        return;
      }
      toast.success("Job created");
      navigate(`/jobs/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not create the job — check the fields and try again.",
      );
    }
  }

  // The customer select is required — surface the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the job form can't be filled in yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  return (
    <EntityForm<JobFormValues>
      title="Create job"
      breadcrumbs={[{ label: "Jobs", href: "/jobs" }, { label: "New" }]}
      defaultValues={{ status: "new" }}
      fields={jobFields(customerOptions)}
      submitLabel="Create job"
      onSubmit={onSubmit}
      cancelHref="/jobs"
      error={error}
      isSubmitting={createJob.isPending}
    />
  );
}
